import { useState } from 'react';
import { useLocale, useT } from '~/i18n';
import { APP_STORE_URL, PLAY_STORE_URL } from '../lib/appStores';

/**
 * «TBit Jobs en tu celular», arriba del panel. El gancho no es la app en sí:
 * es enterarse en el momento en que una empresa contesta (la app avisa con una
 * notificación). Se cierra con la X y no vuelve en ese navegador.
 *
 * Los distintivos son los OFICIALES de cada tienda, sin recolorear (lo piden
 * las guías de Apple y de Google), en `public/stores/`: el de Apple solo existe
 * en inglés en su sitio de desarrolladores; el de Google, en español y en
 * inglés (recortado a su borde para que mida lo mismo que el de Apple).
 *
 * Android todavía no está en Google Play: su distintivo se ve sin enlace y con
 * «Próximamente» debajo, para que quien tiene Android sepa que viene.
 */
const BADGE_HEIGHT = 40;
const DISMISS_KEY = 'tbit.jobapply.appBannerDismissed';

function dismissedBefore(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export default function AppDownloadBanner() {
  const t = useT('workspace').dashboard.appBanner;
  const locale = useLocale();
  const playBadge = locale === 'en' ? '/stores/google-play-en.png' : '/stores/google-play-es.png';
  const [hidden, setHidden] = useState(dismissedBefore);
  if (hidden) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* Sin storage vuelve a salir la próxima vez; no pasa nada. */
    }
    setHidden(true);
  };

  return (
    <section
      aria-label={t.title}
      className="relative flex flex-wrap items-center gap-x-5 gap-y-3 rounded-box py-4 pl-5 pr-11 rise"
      style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline-hi)' }}
    >
      <img src="/brand/mascot-96.png" alt="" width={44} height={44} className="shrink-0" />
      <div className="min-w-0 flex-1 basis-56">
        <p className="font-semibold">{t.title}</p>
        <p className="text-sm hush">{t.lede}</p>
      </div>
      <div className="flex flex-wrap items-start gap-3">
        <a href={APP_STORE_URL} target="_blank" rel="noopener" aria-label={t.appStore}>
          <img src="/stores/app-store.svg" alt={t.appStore} height={BADGE_HEIGHT} style={{ height: BADGE_HEIGHT, width: 'auto' }} />
        </a>
        {PLAY_STORE_URL ? (
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener" aria-label={t.playStore}>
            <img src={playBadge} alt={t.playStore} height={BADGE_HEIGHT} style={{ height: BADGE_HEIGHT, width: 'auto' }} />
          </a>
        ) : (
          <span className="flex flex-col items-center gap-0.5">
            <img src={playBadge} alt={t.playStoreSoon} height={BADGE_HEIGHT} style={{ height: BADGE_HEIGHT, width: 'auto' }} />
            <span className="text-xs hush">{t.soon}</span>
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t.dismiss}
        className="btn btn-ghost btn-xs btn-circle absolute right-2 top-2"
      >
        ×
      </button>
    </section>
  );
}
